import * as systemActions from '../../systemManager/systemActions'
import * as workorderActions from '../workorderActions'
import { Table } from '@atd/rui-base.mui.table'
import { TableBody } from '@atd/rui-base.mui.table-body'
import { TableHead } from '@atd/rui-base.mui.table-head'
import { TableRow } from '@atd/rui-base.mui.table-row'
import { TableCell } from '@atd/rui-base.mui.table-cell'
import { Checkbox } from '@atd/rui-base.mui.checkbox'

import { formatDistanceToNow, isBefore, subMinutes } from 'date-fns'

import { Button } from '@atd/rui-base.mui.button'
import ResponsiveDialog from '../../commonComponents/responsiveDialog'
import DialogHeader from '../../commonComponents/dialogHeader'
import { DialogActions } from '@atd/rui-base.mui.dialog-actions'
import { DialogContent } from '@atd/rui-base.mui.dialog-content'
import React from 'react'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'
import geolib from 'geolib'

const COMPACT_CELLS_SX = {
    '& .MuiTableCell-root:not(.MuiTableCell-paddingCheckbox)': { px: { xs: 1, sm: 2 } },
}

export function getSystemDispatchId(system, useVehicleIds) {
    return useVehicleIds ? system.vehicleId : system.id
}

export function allSystemsHaveVehicleId(systems) {
    return systems.length > 0 && systems.every(system => Boolean(system.vehicleId))
}

function mapStateToProps(state, ownProps) {
    return {
        boundaryPoints: ownProps.boundaryPoints || state.scout.boundaryPoints,
        systems: state.system.dispatchSystems || [],
        selectedCentroid: ownProps.centroid || state.scout.selectedCentroid,
        entrancePoints: ownProps.entrancePoints || state.scout.entrancePoints,
        workorderUuid: ownProps.guid || state.workorder.workorderUuid,
        isThirdPartyWorkorder: !!ownProps.isThirdPartyWorkorder,
    }
}

function mapDispatchToProps(dispatch) {
    return {
        ...bindActionCreators(systemActions, dispatch),
        ...bindActionCreators(workorderActions, dispatch),
    }
}

export class DispatchDialog extends React.Component {
    constructor(props) {
        super(props)
        this.state = {
            open: true,
            selectedOnlineSystems: [],
            selectedOfflineSystems: [],
            onlineSystems: [],
            offlineSystems: [],
            useVehicleIds: false,
            message: '',
        }
    }

    componentDidMount() {
        this.props.fetchDispatchSystems()
        this.sortSystemLists(this.props.systems)
    }

    componentDidUpdate(prevProps) {
        if (prevProps.systems !== this.props.systems ||
            prevProps.selectedCentroid !== this.props.selectedCentroid ||
            prevProps.entrancePoints !== this.props.entrancePoints) {
            this.sortSystemLists(this.props.systems)
        }
    }

    sortSystemLists = (systems) => {
        let dispatchSystems = systems
        if (this.props.isThirdPartyWorkorder) {
            dispatchSystems = systems.filter(system => system.thirdPartyDispatchCapable)
        }

        const online = []
        const offline = []
        const fiveMinAgo = subMinutes(new Date(), 5)

        for (const system of dispatchSystems) {
            if (isBefore((new Date(system.lastSeen)), fiveMinAgo)) {
                offline.push(system)
            }
            else {
                online.push(system)
            }
        }
        this.sortByDistance(online)
        this.sortByDistance(offline)
        this.setState({
            onlineSystems: [...online],
            offlineSystems: [...offline],
            useVehicleIds: allSystemsHaveVehicleId([...online, ...offline]),
        })
    }

    sortByDistance = (systems) => {
        if (systems.length === 0) return systems

        let refCoord

        // TODO HANDLE HERE
        if (this.props.entrancePoints.length === 1) { // array of entrances
            refCoord = {latitude: this.props.entrancePoints[0][1], longitude: this.props.entrancePoints[0][0]}
        }
        else if (this.props.selectedCentroid.length > 0) { // only one center allowed
            refCoord = {latitude: this.props.selectedCentroid[1], longitude: this.props.selectedCentroid[0]}
        }
        else if (this.props.boundaryPoints.length || this.props.entrancePoints.length) {
            const eCoords = this.props.entrancePoints.map(point => {
                return {latitude: point[1], longitude: point[0]}
            })
            const bCoords = this.props.boundaryPoints.map(point => {
                return {latitude: point[1], longitude: point[0]}
            })
            refCoord = geolib.getCenter([...eCoords, ...bCoords])
        }
        else {
            return systems
        }

        for (const system of systems) {
            if (system.gpsLatitude && system.gpsLongitude) {
                // get distance from field in meters, convert to km
                system.distance = geolib.getDistance(refCoord, {latitude: system.gpsLatitude, longitude: system.gpsLongitude}) / 1000
            }
        }
        systems.sort((a, b) => {
            if (!a.distance && !b.distance) return 0
            if (!a.distance) return 1
            if (!b.distance) return -1

            return a.distance - b.distance
        })
    }

    handleChange = (event, index, value) => {
        this.setState({value})
    }

    handleDispatch = () => {
        const selectedIds = [...this.state.selectedOnlineSystems, ...this.state.selectedOfflineSystems]
        const useVehicleIds = this.state.useVehicleIds
        const systemIds = useVehicleIds ? [] : selectedIds
        const vehicleIds = useVehicleIds ? selectedIds : []

        this.props.dispatchWorkorder(this.props.workorderUuid, systemIds, vehicleIds).then(rVal => {
            if (!rVal) {
                this.setState({message: 'Error while dispatching'})
            }
            else {
                this.requestClose(this.props.onDispatch)
            }
        })
    }
    requestClose = (onComplete) => {
        this.exitCallback = onComplete
        this.setState({open: false})
    }
    handleCloseDialog = () => {
        this.requestClose(this.props.onClose)
    }
    toggleSystem = (listKey, id) => {
        this.setState(prev => {
            const selected = prev[listKey]

            return {
                [listKey]: selected.includes(id) ? selected.filter(s => s !== id) : [...selected, id],
            }
        })
    }
    toggleAllSystems = (listKey, systems, checked) => {
        const useVehicleIds = this.state.useVehicleIds
        this.setState({ [listKey]: checked ? systems.map(system => getSystemDispatchId(system, useVehicleIds)) : [] })
    }

    mapSystemToRow = (system, listKey, selectedIds, useVehicleIds) => {
        const dispatchId = getSystemDispatchId(system, useVehicleIds)

        return (
            <TableRow
                key={dispatchId}
                selected={selectedIds.includes(dispatchId)}
                name={dispatchId}
                hover
                onClick={() => this.toggleSystem(listKey, dispatchId)}
                sx={{ cursor: 'pointer' }}
            >
                <TableCell padding="checkbox" align="left" sx={{ pl: 0, width: 'auto' }} onClick={(e) => e.stopPropagation()}>
                    <Checkbox
                        size="small"
                        sx={{ p: 0.25 }}
                        checked={selectedIds.includes(dispatchId)}
                        onChange={() => this.toggleSystem(listKey, dispatchId)}
                        name={`select-${dispatchId}`}
                    />
                </TableCell>
                {this.createSystemRowColumns(system)}
            </TableRow>
        )
    }
    createSystemRowColumns = (system) => {
        const hasDistance = !isNaN(system.distance)
        const hasGps = Boolean(system.lastSeen)

        return [
            <TableCell key="name">
                { system.systemName || system.name || system.systemId || system.vehicleId || system.id }
            </TableCell>,
            <TableCell key="distance" align={hasDistance ? 'inherit' : 'center'}>
                {hasDistance ? system.distance.toFixed(2) : '-'}
            </TableCell>,
            <TableCell key="lastGps" align={hasGps ? 'inherit' : 'center'}>
                {hasGps ? formatDistanceToNow(new Date(system.lastSeen + 'Z'), { addSuffix: true }) : '-'}
            </TableCell>,
        ]
    }

    createOnlineTableSection = () => {
        let section
        if (this.state.onlineSystems.length > 0) {
            const useVehicleIds = this.state.useVehicleIds
            const systemRows = this.state.onlineSystems.map(system => this.mapSystemToRow(system, 'selectedOnlineSystems', this.state.selectedOnlineSystems, useVehicleIds))
            section =
                <div name="onlineSystemSection">
                    <h3>Online Systems</h3>
                    <Table
                        name="onlineSystemTable"
                        sx={COMPACT_CELLS_SX}
                    >
                        {this.systemTableContent(systemRows, this.state.onlineSystems, this.state.selectedOnlineSystems, 'selectedOnlineSystems')}
                    </Table>
                </div>
        }
        else {
            section = <div><h3>Online Systems</h3><p>No available Systems</p></div>
        }

        return section
    }
    createOfflineTableSection = () => {
        let section
        if (this.state.offlineSystems.length > 0) {
            const useVehicleIds = this.state.useVehicleIds
            const systemRows = this.state.offlineSystems.map(system => this.mapSystemToRow(system, 'selectedOfflineSystems', this.state.selectedOfflineSystems, useVehicleIds))
            section =
                <div name="offlineSystemSection">
                    <h3>Offline Systems</h3>
                    <Table
                        name="offlineSystemTable"
                        sx={COMPACT_CELLS_SX}
                    >
                        {this.systemTableContent(systemRows, this.state.offlineSystems, this.state.selectedOfflineSystems, 'selectedOfflineSystems')}
                    </Table>
                </div>
        }
        else {
            section = <div><h3>Offline Systems</h3><p>No available Systems</p></div>
        }

        return section
    }
    systemTableContent = (systemRows, systems, selectedIds, listKey) => {
        const allChecked = systems.length > 0 && selectedIds.length === systems.length
        const someChecked = selectedIds.length > 0 && !allChecked

        return [
            <TableHead key="header">
                <TableRow sx={{'& th': {whiteSpace: 'nowrap'}}}>
                    <TableCell padding="checkbox" align="left" sx={{ pl: 0, width: 'auto' }}>
                        <Checkbox
                            size="small"
                            sx={{ p: 0.25 }}
                            checked={allChecked}
                            indeterminate={someChecked}
                            onChange={(e) => this.toggleAllSystems(listKey, systems, e.target.checked)}
                            name="selectAll"
                        />
                    </TableCell>
                    <TableCell>System</TableCell>
                    <TableCell>{'Distance (km)'}</TableCell>
                    <TableCell>{'Last GPS'}</TableCell>
                </TableRow>
            </TableHead>,
            <TableBody key="body">
                {systemRows}
            </TableBody>,
        ]
    }

    render() {
        const onlineSection = this.createOnlineTableSection()
        const offlineSection = this.createOfflineTableSection()
        let thirdPartyNotice = null
        if (this.props.isThirdPartyWorkorder) {
            thirdPartyNotice = (
                <div
                    name="thirdPartyDispatchNotice"
                    style={{
                        backgroundColor: '#eef6ff',
                        border: '1px solid #8ab8e6',
                        borderRadius: '4px',
                        color: '#1a4b7a',
                        margin: '12px 0',
                        padding: '10px 12px',
                    }}
                >
                    This job was generated externally. Only systems capable of dispatching this job type are shown.
                </div>
            )
        }
        const actions = [
            <Button variant="text"
                key="cancelDispatch"
                color="primary"
                onClick={this.handleCloseDialog}
                name="cancelDispatch">Cancel</Button>,
            <Button variant="contained"
                key="submitDispatch"
                color="primary"
                onClick={this.handleDispatch}
                disabled={this.state.selectedOnlineSystems.length === 0 && this.state.selectedOfflineSystems.length === 0}
                name="submitDispatch">Send to System</Button>,
        ]

        return (
            <ResponsiveDialog
                open={this.state.open}
                onClose={(_, reason) => { if (reason !== 'backdropClick') this.requestClose(this.props.onClose) }}
                TransitionProps={{onExited: () => { if (this.exitCallback) this.exitCallback() }}}
                PaperProps={{className: 'wodialog'}}
                fullWidth
                name="dispatchDialog"
            >
                <DialogHeader onClose={this.handleCloseDialog}>Send to the Selected Systems</DialogHeader>
                <DialogContent>
                    {thirdPartyNotice}
                    {onlineSection}
                    {offlineSection}
                    <div style={{color: 'red'}}>{this.state.message}</div>
                </DialogContent>
                <DialogActions>{actions}</DialogActions>
            </ResponsiveDialog>
        )
    }
}

export default connect(mapStateToProps, mapDispatchToProps)(DispatchDialog)
